import { Box, Stack, Typography, TypographyProps } from "@mui/material";
import { CSSProperties, useLayoutEffect, useRef, useState } from "react";
import { MprcLink } from "./MprcLink";
import { useMprcTheme } from "./MprcThemeProvider";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    maxRows: number;
    component?: React.ElementType;
    readMoreText?: string;
    readLessText?: string;
}

export type MprcTypographyOnDemandProps = IProps & Omit<TypographyProps, "noWrap" | "component" | "sx">;

////////////////////////////////////////////////////////////////////////////

export const MprcTypographyOnDemand: React.FC<MprcTypographyOnDemandProps> = ({ children, maxRows, readMoreText, readLessText, component, ...rest }) => {

    const { theme } = useMprcTheme();

    const [isExpandable, setIsExpandable] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const [lineHeight, setLineHeight] = useState<number>(0);

    const contentRef = useRef<HTMLDivElement>(null);
    const contentStyle: CSSProperties = isExpanded ? {} : {
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: maxRows
    };

    ////////////////////////////////////////////////////////////////////////////

    useLayoutEffect(() => {
        if (contentRef.current) {

            const computedStyle = window.getComputedStyle(contentRef.current);
            const lineSize = Number(computedStyle['line-height' as any].replace('px', ''));
            setLineHeight(lineSize);

            const contentHeight = contentRef.current.getBoundingClientRect().height;
            if (contentHeight > (maxRows * lineSize)) {
                setIsExpandable(true);
                setIsExpanded(false);
            }
        }
    }, [maxRows]);

    return (
        <Stack direction="column" spacing={1}>
            <Box sx={{ height: isExpanded ? 'auto' : (maxRows * lineHeight) }}>
                <Typography
                    ref={contentRef}
                    sx={{ ...contentStyle }}
                    component={component ?? "div"}
                    {...rest}
                >
                    {children}
                </Typography>
            </Box>
            {isExpandable &&
                <MprcLink variant="captionHeader" color="inherit" onClick={() => setIsExpanded(prev => !prev)} fontWeight="bold">
                    {isExpanded ? readLessText ?? theme.MprcTypographyOnDemand.readLess : readMoreText ?? theme.MprcTypographyOnDemand.readMore}
                </MprcLink>
            }
        </Stack>
    );
};


/* Ref:
https://ilxanlar.medium.com/ellipsis-the-art-of-truncation-in-web-applications-8b141ce33774
https://gist.github.com/ilxanlar/87cc2d9881fa097e1eab412784bc6055#file-truncate-html-line-count-js
*/
